import axios from 'axios';
// Assuming your API base URL is configured in config.js or environment variables
import { API_URL } from '../utils/config'; // Correct import path and variable name

// Construct URL assuming API_URL might already contain /api
const WISHLIST_API_URL = `${API_URL}/wishlist`; // Remove the extra /api prefix

// Helper function to get the auth token (adjust based on your auth implementation)
const getAuthHeaders = () => {
  // Try both 'token' and 'authToken' for compatibility
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (!token) {
    return {}; // Return empty object for guests (no Authorization header)
  }
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
    }
  };
};

/**
 * Fetches the user's wishlist items from the backend.
 * Supports both authenticated users and guests (via device token or session ID).
 * @param {string} deviceToken - Optional device token for guest wishlists
 * @returns {Promise<Array>} A promise that resolves to an array of wishlist items.
 */
export const fetchWishlist = async (deviceToken = null) => {
  const authHeaders = getAuthHeaders();

  // Build query params for guest users
  const params = {};
  if (!localStorage.getItem('token')) {
    if (deviceToken) {
      params.deviceToken = deviceToken;
    } else {
      // Fallback to session ID if no device token
      const sessionId = sessionStorage.getItem('eladore_session_id');
      if (sessionId) {
        params.sessionId = sessionId;
      }
    }
  }

  try {
    const response = await axios.get(WISHLIST_API_URL, {
      ...authHeaders,
      params
    });
    return response.data; // Assuming the backend returns the array of items directly
  } catch (error) {
    console.error('Error fetching wishlist:', error.response ? error.response.data : error.message);
    throw error; // Re-throw the error to be caught by the calling component/context
  }
};

/**
 * Adds a product to the user's wishlist on the backend.
 * Supports both authenticated users and guests (via device token or session ID).
 * @param {number|string} productId The ID of the product to add.
 * @param {string} deviceToken - Optional device token for guest wishlists
 * @returns {Promise<Object>} A promise that resolves to the response data (e.g., confirmation message and item).
 */
export const addItemToWishlist = async (productId, deviceToken = null) => {
  if (!productId) {
    console.error('Product ID is required to add to wishlist.');
    throw new Error('Product ID is required.');
  }

  const payload = { productId };
  if (!localStorage.getItem('token')) {
    if (deviceToken) {
      payload.deviceToken = deviceToken;
    } else {
      // Fallback to session ID if no device token
      const sessionId = sessionStorage.getItem('eladore_session_id');
      if (sessionId) {
        payload.sessionId = sessionId;
      }
    }
  }

  try {
    const response = await axios.post(WISHLIST_API_URL, payload, getAuthHeaders());
    return response.data;
  } catch (error) {
    console.error('Error adding item to wishlist:', error.response ? error.response.data : error.message);
    // Handle specific errors (e.g., 409 Conflict for duplicate)
    if (error.response && error.response.status === 409) {
      //console.log('Item already in wishlist (API).');
      // Return a specific indicator or the error response data
      return error.response.data;
    }
    throw error;
  }
};

/**
 * Removes a product from the user's wishlist on the backend.
 * Supports both authenticated users and guests (via device token or session ID).
 * @param {number|string} productId The ID of the product to remove.
 * @param {string} deviceToken - Optional device token for guest wishlists
 * @returns {Promise<Object>} A promise that resolves to the response data (e.g., confirmation message).
 */
export const removeItemFromWishlist = async (productId, deviceToken = null) => {
  if (!productId) {
    console.error('Product ID is required to remove from wishlist.');
    throw new Error('Product ID is required.');
  }

  const params = {};
  if (!localStorage.getItem('token')) {
    if (deviceToken) {
      params.deviceToken = deviceToken;
    } else {
      // Fallback to session ID if no device token
      const sessionId = sessionStorage.getItem('eladore_session_id');
      if (sessionId) {
        params.sessionId = sessionId;
      }
    }
  }

  try {
    const response = await axios.delete(`${WISHLIST_API_URL}/${productId}`, {
      ...getAuthHeaders(),
      params
    });
    return response.data;
  } catch (error) {
    console.error('Error removing item from wishlist:', error.response ? error.response.data : error.message);
    // Handle specific errors (e.g., 404 Not Found)
    if (error.response && error.response.status === 404) {
      //console.log('Item not found in wishlist to remove (API).');
      // Return a specific indicator or the error response data
      return error.response.data;
    }
    throw error;
  }
};

/**
 * Merges guest wishlist to user account when user logs in.
 * @param {string} deviceToken - Device token to merge from
 * @returns {Promise<Object>} A promise that resolves to the merge response data.
 */
export const mergeGuestWishlist = async (deviceToken) => {
  if (!deviceToken) {
    console.error('Device token is required to merge wishlist.');
    throw new Error('Device token is required.');
  }

  try {
    const response = await axios.post(`${WISHLIST_API_URL}/merge`,
      { deviceToken },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {
    console.error('Error merging guest wishlist:', error.response ? error.response.data : error.message);
    throw error;
  }
};

// Export an object containing the service functions
const wishlistService = {
  fetchWishlist,
  addItemToWishlist,
  removeItemFromWishlist,
  mergeGuestWishlist,
};

export default wishlistService;